import { makeAutoObservable, runInAction } from "mobx";
import type { ShellPreferenceCache } from "./preferences/shell-preference-cache";
import { browserShellPreferenceCache } from "./preferences/shell-preference-cache";
import type { ShellMessages } from "./messages/message-types";

export type LocaleCode = "de" | "en";
export type MessageKey = keyof ShellMessages;

const messageLoaders: Record<
  LocaleCode,
  () => Promise<{ default: ShellMessages }>
> = {
  de: () => import("./messages/de"),
  en: () => import("./messages/en"),
};

function browserLocale(): LocaleCode {
  if (typeof navigator !== "undefined" && navigator.language.startsWith("de")) {
    return "de";
  }

  return "en";
}

function isLocaleCode(value: string | undefined): value is LocaleCode {
  return value === "de" || value === "en";
}

export class LocaleStore {
  code: LocaleCode;
  messages: ShellMessages | undefined;
  loadFailed = false;
  fallbackMessages: ShellMessages | undefined;

  constructor(
    initialLocale?: LocaleCode,
    private readonly cache: ShellPreferenceCache = browserShellPreferenceCache,
  ) {
    const cached = cache.read("locale");
    this.code = initialLocale ?? (isLocaleCode(cached) ? cached : browserLocale());
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async load(): Promise<void> {
    const requestedLocale = this.code;
    this.loadFailed = false;
    if (typeof document !== "undefined") document.documentElement.lang = requestedLocale;
    try {
      const [module, fallback] = await Promise.all([
        messageLoaders[requestedLocale](),
        messageLoaders.en(),
      ]);
      if (this.code === requestedLocale) {
        runInAction(() => {
          this.messages = module.default;
          this.fallbackMessages = fallback.default;
        });
      }
    } catch {
      const fallback = await messageLoaders.en();
      if (this.code === requestedLocale) {
        runInAction(() => {
          this.messages = fallback.default;
          this.fallbackMessages = fallback.default;
          this.loadFailed = true;
        });
      }
    }
  }

  async setLocale(code: LocaleCode): Promise<void> {
    /*
     * Persist first, and persist unconditionally.
     *
     * The write used to sit after `await this.load()` and behind an early
     * return for "already this locale". Both lost real choices: a German
     * operator on a German browser who pressed "Deutsch" to pin it stored
     * nothing at all — the app had merely guessed right from `navigator`, so
     * the next machine guessed again — and a choice made just before a reload
     * could be overtaken by the message bundle still loading. Which language
     * the operator wants is settled the moment they press the button; it does
     * not depend on a dynamic import resolving.
     */
    this.cache.write("locale", code);

    if (code === this.code && this.messages) {
      return;
    }

    this.code = code;
    this.messages = undefined;

    if (typeof document !== "undefined") {
      document.documentElement.lang = code;
    }

    await this.load();
  }

  text(key: MessageKey): string {
    return this.messages?.[key] ?? this.fallbackMessages?.[key] ?? key;
  }
}
